import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/card';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ChevronLeft, Mail, CreditCard, Truck, AlertCircle, Expand, ChevronDown } from 'lucide-react';
import { CATEGORY_LABELS, PRICING_MODEL_LABELS } from '../../utils/planningConstants';
import { formatDateDisplay, formatTimeDisplay } from '../../utils/planningHelpers';

export default function ReviewSection({ hub }) {
  const {
    cart, eventDetails, quote, setShowReview, setShowEmailQuoteModal, setShowCheckoutModal,
    paymentType, setPaymentType, installmentCount, setInstallmentCount,
    installmentDates, setInstallmentDates, autoCharge, setAutoCharge,
    hasDropoffItems, expandedItems, setExpandedItems
  } = hub;

  // Don't render if quote is not ready
  if (!quote || !quote.items || quote.subtotal === undefined) {
    return (
      <div className="max-w-4xl mx-auto px-0 sm:px-4">
        <Button variant="ghost" onClick={() => setShowReview(false)} className="mb-4 sm:mb-6"><ChevronLeft className="mr-2 h-4 w-4" /> Back to Services</Button>
        <Card data-testid="review-section">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Loading quote...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-0 sm:px-4">
      <Button variant="ghost" onClick={() => setShowReview(false)} className="mb-4 sm:mb-6"><ChevronLeft className="mr-2 h-4 w-4" /> Back to Services</Button>

      <Card data-testid="review-section">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl">Review Your Event</CardTitle>
          <CardDescription>Confirm your selections before booking</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-4 sm:p-6 pt-0 sm:pt-0">
          {/* Event Details */}
          <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
            <h3 className="font-semibold mb-3 text-sm sm:text-base">Event Details</h3>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
              <div><span className="text-muted-foreground">Date</span><p className="font-medium">{formatDateDisplay(eventDetails.date)}</p></div>
              <div><span className="text-muted-foreground">Time</span><p className="font-medium">{formatTimeDisplay(eventDetails.time, eventDetails.duration)}</p></div>
              <div><span className="text-muted-foreground">Guests</span><p className="font-medium">{eventDetails.guestCount} people</p></div>
              <div><span className="text-muted-foreground">Location</span><p className="font-medium">ZIP: {eventDetails.venueZipCode}</p></div>
            </div>
          </div>

          {/* Selected Services */}
          <div>
            <h3 className="font-semibold mb-3">Selected Services</h3>
            <div className="space-y-4">
              {Object.entries(
                quote.items.reduce((g, item) => { const c = item.category || 'other'; if (!g[c]) g[c] = []; g[c].push(item); return g; }, {})
              ).map(([category, items]) => (
                <div key={category}>
                  <p className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-2">{CATEGORY_LABELS[category] || category}</p>
                  <div className="space-y-3">
                    {items.map((item, idx) => {
                      const cartItem = cart.find(c => c.service_id === item.service_id);
                      const isExpanded = expandedItems[item.service_id];
                      let quantityText = `Qty: ${item.quantity}`;
                      if (item.pricing_model === 'per_person' || item.pricing_model === 'per_hour_per_person')
                        quantityText = `${item.guest_count || cartItem?.guestCount || eventDetails.guestCount} guests`;
                      if (item.pricing_model === 'per_hour')
                        quantityText = `${item.hours || cartItem?.hours || eventDetails.duration || 4} hours`;
                      if (item.pricing_model === 'per_hour_per_person')
                        quantityText = `${item.guest_count || cartItem?.guestCount || eventDetails.guestCount} guests × ${item.hours || cartItem?.hours || eventDetails.duration || 4} hrs`;

                      return (
                        <div key={idx} className={`border rounded-lg overflow-hidden ${item.is_dropoff ? 'border-emerald-300 bg-emerald-50/50' : ''}`}>
                          <div className="flex items-start sm:items-center p-3 gap-3 sm:gap-4">
                            {cartItem?.image_url && <img src={cartItem.image_url} alt={item.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                <p className="font-medium text-sm sm:text-base">{item.name}</p>
                                {item.is_dropoff && <Badge className="bg-emerald-500 text-[10px] sm:text-xs"><Truck className="h-3 w-3 mr-1" />Drop-off</Badge>}
                              </div>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {item.is_dropoff ? `Qty: ${item.quantity}` : quantityText} • {item.is_dropoff ? 'Flat Rate' : PRICING_MODEL_LABELS[item.pricing_model]}
                              </p>
                              {!item.is_dropoff && item.selected_options?.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {item.selected_options.map((opt, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">{opt.choice_name} {opt.price > 0 && `(+$${opt.price.toFixed(2)})`}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold">${(item.total_price || item.total || 0).toFixed(2)}</p>
                              <button onClick={() => setExpandedItems(prev => ({ ...prev, [item.service_id]: !prev[item.service_id] }))} className="text-xs text-indigo-500 flex items-center gap-1 mt-1">
                                <Expand className="h-3 w-3" /> {isExpanded ? 'Less' : 'Details'}
                              </button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-3 pb-3 pt-0 border-t bg-muted/30 text-xs space-y-1">
                              <div className="flex justify-between"><span>Base price</span><span>${(item.base_price || 0).toFixed(2)}</span></div>
                              {(item.options_price || 0) > 0 && <div className="flex justify-between"><span>Options</span><span>+${(item.options_price || 0).toFixed(2)}</span></div>}
                              <div className="flex justify-between"><span>Subtotal</span><span>${(item.calculated_price || item.subtotal || 0).toFixed(2)}</span></div>
                              {(item.service_fee || 0) > 0 && <div className="flex justify-between"><span>Service fee</span><span>${(item.service_fee || 0).toFixed(2)}</span></div>}
                              {(item.tax || 0) > 0 && <div className="flex justify-between"><span>Tax</span><span>${(item.tax || 0).toFixed(2)}</span></div>}
                              <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>${(item.total_price || item.total || 0).toFixed(2)}</span></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Price Summary & Payment */}
          <div className="border-t pt-6">
            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(quote.subtotal || 0).toFixed(2)}</span></div>
              {(quote.admin_service_fee || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Service Fee <span className="text-xs text-muted-foreground">(Food &amp; Beverages)</span></span>
                  <span>${(quote.admin_service_fee || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm"><span>Tax ({quote.tax_rate || 0}%)</span><span>${(quote.tax || quote.tax_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between font-bold text-xl border-t pt-2"><span>Total</span><span>${(quote.total || 0).toFixed(2)}</span></div>
            </div>

            <h3 className="font-semibold mb-3">Choose Payment Method</h3>
            <div className="space-y-3">
              {/* Full Payment */}
              <div onClick={() => setPaymentType('full')} className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${paymentType === 'full' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border hover:border-indigo-300'}`} data-testid="payment-option-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentType === 'full' ? 'border-indigo-500' : 'border-muted-foreground'}`}>
                      {paymentType === 'full' && <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />}
                    </div>
                    <div><p className="font-semibold">Pay in Full</p><p className="text-xs text-muted-foreground">One-time payment</p></div>
                  </div>
                  <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">${(quote.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Plan */}
              <div onClick={() => setPaymentType('plan')} className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${paymentType === 'plan' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border hover:border-indigo-300'}`} data-testid="payment-option-plan">
                <div className="flex items-start sm:items-center justify-between gap-2">
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0 ${paymentType === 'plan' ? 'border-indigo-500' : 'border-muted-foreground'}`}>
                      {paymentType === 'plan' && <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />}
                    </div>
                    <div><p className="font-semibold text-sm sm:text-base">Payment Plan</p><p className="text-[10px] sm:text-xs text-muted-foreground">50% deposit now, pay the rest on your schedule</p></div>
                  </div>
                  <span className="text-base sm:text-lg font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">${(quote.deposit_amount || 0).toFixed(2)} <span className="text-[10px] sm:text-xs font-normal text-muted-foreground">today</span></span>
                </div>

                {paymentType === 'plan' && (
                  <div className="mt-4 pt-4 border-t space-y-4" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-between items-center bg-white dark:bg-slate-900/50 rounded-lg p-3 border">
                      <div><p className="font-semibold text-indigo-600 dark:text-indigo-400">Due Today — 50% Deposit</p><p className="text-xs text-muted-foreground">Secures your date and services</p></div>
                      <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">${(quote.deposit_amount || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Split remaining ${(quote.balance_due || 0).toFixed(2)} into:</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4].map(n => (
                          <Button key={n} type="button" variant={installmentCount === n ? 'default' : 'outline'} size="sm" onClick={() => setInstallmentCount(n)} data-testid={`installment-count-${n}`}>
                            {n} Payment{n > 1 ? 's' : ''}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Payment Schedule</label>
                      {installmentDates.map((inst, idx) => (
                        <div key={idx} className="flex items-center gap-2 sm:gap-3 bg-white dark:bg-slate-900/50 rounded-lg p-2 sm:p-3 border" data-testid={`installment-row-${idx}`}>
                          <div className="flex-1 min-w-0">
                            <Input type="date" value={inst.date} min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => { const u = [...installmentDates]; u[idx] = { ...u[idx], date: e.target.value }; setInstallmentDates(u); }}
                              className="text-xs sm:text-sm" data-testid={`installment-date-${idx}`} />
                          </div>
                          <div className="w-20 sm:w-28 flex-shrink-0">
                            <Input type="number" value={inst.amount} step="0.01" min="1"
                              onChange={(e) => { const u = [...installmentDates]; u[idx] = { ...u[idx], amount: parseFloat(e.target.value) || 0 }; setInstallmentDates(u); }}
                              className="text-sm text-right" data-testid={`installment-amount-${idx}`} />
                          </div>
                        </div>
                      ))}
                      {installmentDates.length > 0 && Math.abs(installmentDates.reduce((s, i) => s + i.amount, 0) - (quote.balance_due || 0)) > 0.02 && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Installments must total ${(quote.balance_due || 0).toFixed(2)} (currently ${installmentDates.reduce((s, i) => s + i.amount, 0).toFixed(2)})
                        </p>
                      )}
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer bg-white dark:bg-slate-900/50 rounded-lg p-3 border">
                      <Checkbox checked={autoCharge} onCheckedChange={(checked) => setAutoCharge(checked)} data-testid="auto-charge-toggle" />
                      <div>
                        <span className="font-medium text-sm">Auto-pay on scheduled dates</span>
                        <p className="text-xs text-muted-foreground">Your card will be charged automatically on each date.</p>
                      </div>
                    </label>
                    <p className="text-xs text-muted-foreground">All payments must be completed at least 14 days before your event.</p>
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground border-t pt-2">
                Full refund if cancelled 30+ days before event. 50% refund 15-29 days. Non-refundable within 14 days.
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex-col sm:flex-row gap-3">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => setShowEmailQuoteModal(true)} data-testid="email-quote-btn">
            <Mail className="mr-2 h-4 w-4" /> Email Me My Quote
          </Button>
          <Button className="w-full sm:flex-1" size="lg" onClick={() => setShowCheckoutModal(true)} data-testid="pay-now-btn">
            <CreditCard className="mr-2 h-4 w-4" />
            {paymentType === 'full' ? `Pay Now — $${(quote.total || 0).toFixed(2)}` : `Pay Deposit — $${(quote.deposit_amount || 0).toFixed(2)}`}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
